import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API_BASE =
  (window._env_ && window._env_.REACT_APP_API_URL) ||
  process.env.REACT_APP_API_URL ||
  '/api';

// ─── PALETA ───────────────────────────────────────────────────────────────────
const C = {
  amarillo:   '#F5C200', amarilloOsc:'#D4A800', amarilloBg:'#FFF9E6',
  rojo:       '#7A1010', rojoClaro:  '#9B1515',
  crema:      '#F5F0E8', cremaDark:  '#EDE8DF',
  blanco:     '#FFFFFF', textoOsc:   '#3D0808', textoSub: '#A07050',
  borde:      '#E8D8B0', bordeOsc:   '#C8A850',
  verde:      '#1B6B2A', verdeBg:    '#E8F5E9', verdeClaro:'#2E7D32',
  azul:       '#1565C0', azulBg:     '#E3F2FD',
  error:      '#C62828', errorBg:    '#FFEBEE',
  sombra:     '0 2px 8px rgba(122,16,16,0.10)',
  sombraF:    '0 6px 20px rgba(122,16,16,0.18)',
};

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background:${C.crema}; font-family:'Inter',system-ui,sans-serif; color:${C.textoOsc}; -webkit-font-smoothing:antialiased; }
  ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-track{background:${C.crema}} ::-webkit-scrollbar-thumb{background:${C.bordeOsc};border-radius:3px}
  input,select,textarea{outline:none;font-family:inherit} input::placeholder{color:${C.textoSub}88}
  table{border-collapse:collapse;width:100%} th{text-align:left}
  *{-webkit-tap-highlight-color:transparent}
`;

// ─── API — con renovación automática de token ─────────────────────────────────
function getToken() { return localStorage.getItem('token') || ''; }
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

async function apiFetch(path, opts = {}) {
  const url = `${API_BASE}${path}`;
  const headers = { ...authH(), ...(opts.headers || {}) };
  const res = await fetch(url, { ...opts, headers });
  if (res.status === 401) {
    // Token expirado — limpiar y recargar para pedir login
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    window.location.reload();
    return {};
  }
  return res.json();
}

const api = {
  get:   (p)    => apiFetch(p),
  post:  (p, b) => apiFetch(p, { method:'POST',  headers:{'Content-Type':'application/json'}, body:JSON.stringify(b) }),
  put:   (p, b) => apiFetch(p, { method:'PUT',   headers:{'Content-Type':'application/json'}, body:JSON.stringify(b) }),
  patch: (p, b) => apiFetch(p, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify(b) }),
  del:   (p)    => apiFetch(p, { method:'DELETE' }),
};

const fmt = (n) => new Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS',maximumFractionDigits:0}).format(n||0);

// ─── REDONDEO ─────────────────────────────────────────────
// Lee la unidad de redondeo configurada (default: 100)
function getRedondeo() { return parseInt(localStorage.getItem('redondeo')||'100')||100; }
function redondear(monto, unidad) {
  if (!unidad || unidad <= 1) return Math.round(monto);
  return Math.round(monto / unidad) * unidad;
}
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-AR') : '';
// Para fechas que representan "días" guardados en UTC (aperturas de caja)
const fmtDateUTC = (d) => d ? new Date(d).toLocaleDateString('es-AR', { timeZone: 'UTC' }) : '';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('es-AR',{hour:'2-digit',minute:'2-digit'}) : '';

// ─── TOAST ────────────────────────────────────────────────────────────────────
function Toast({ msg, type, onClose }) {
  useEffect(()=>{const t=setTimeout(onClose,3000);return()=>clearTimeout(t);},[onClose]);
  return <div style={{position:'fixed',bottom:80,left:'50%',transform:'translateX(-50%)',zIndex:9999,background:type==='error'?C.error:C.verde,color:'#fff',padding:'12px 28px',borderRadius:12,fontWeight:700,fontSize:15,boxShadow:C.sombraF,whiteSpace:'nowrap'}}>{msg}</div>;
}

// ─── MODAL ────────────────────────────────────────────────────────────────────
function Modal({ title, children, onClose }) {
  return (
    <div style={{position:'fixed',inset:0,background:'#0006',zIndex:500,display:'flex',alignItems:'flex-end',justifyContent:'center'}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.blanco,borderRadius:'20px 20px 0 0',padding:24,width:'100%',maxWidth:600,maxHeight:'88vh',overflowY:'auto',boxShadow:C.sombraF,border:`1px solid ${C.borde}`}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}>
          <div style={{fontWeight:900,fontSize:18,color:C.rojo}}>{title}</div>
          <button onClick={onClose} style={{background:C.cremaDark,border:'none',borderRadius:8,padding:'4px 12px',color:C.textoSub,fontSize:20,lineHeight:1}}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── UI BASE ──────────────────────────────────────────────────────────────────
const inputSt = {width:'100%',background:C.blanco,border:`1.5px solid ${C.borde}`,borderRadius:10,padding:'11px 14px',color:C.textoOsc,fontSize:15,marginBottom:12};
const labelSt = {fontSize:11,color:C.textoSub,fontWeight:700,letterSpacing:1.2,marginBottom:5,display:'block'};

const Btn = ({children,onClick,color=C.rojo,disabled})=>(
  <button onClick={onClick} disabled={disabled} style={{background:disabled?'#CCC':color,color:'#fff',border:'none',borderRadius:10,padding:'12px 20px',fontWeight:800,fontSize:15,width:'100%',marginTop:6,boxShadow:disabled?'none':`0 3px 10px ${color}55`}}>{children}</button>
);
const BtnSm = ({children,onClick,color=C.rojo,outline=false})=>(
  <button onClick={onClick} style={{background:outline?'transparent':color,color:outline?color:'#fff',border:`2px solid ${color}`,borderRadius:20,padding:'7px 18px',fontWeight:700,fontSize:13,boxShadow:outline?'none':`0 2px 8px ${color}44`}}>{children}</button>
);
const PrecioChip = ({value})=>(
  <div style={{display:'inline-block',background:C.amarillo,color:C.rojo,fontWeight:900,fontSize:15,borderRadius:8,padding:'4px 12px'}}>{value}</div>
);
const tipoColor = {
  entero:{bg:C.amarillo,text:C.rojo},trozo:{bg:C.rojoClaro,text:'#fff'},
  elaborado:{bg:'#6A1B9A',text:'#fff'},menudencia:{bg:'#E65100',text:'#fff'},
  fresco:{bg:C.rojoClaro,text:'#fff'},elaborados:{bg:'#6A1B9A',text:'#fff'},
  otro:{bg:C.borde,text:C.textoOsc}
};
// Helper para obtener el tipo/slug de un producto
const getTipo = (p) => p.categoriaid?.slug || p.productotipo || 'otro';
const getTipoNombre = (p) => p.categoriaid?.nombre || p.productotipo || 'otro';

function SubTab({tabs,active,onChange}) {
  return (
    <div style={{display:'flex',background:C.blanco,borderBottom:`2px solid ${C.borde}`,flexShrink:0}}>
      {tabs.map(([k,l])=>(
        <button key={k} onClick={()=>onChange(k)} style={{flex:1,padding:'12px 8px',background:'none',border:'none',color:active===k?C.rojo:C.textoSub,fontWeight:active===k?800:500,fontSize:14,borderBottom:active===k?`3px solid ${C.rojo}`:'3px solid transparent'}}>{l}</button>
      ))}
    </div>
  );
}

// ─── RADIO MEDIO DE PAGO ──────────────────────────────────────────────────────
function RadioPago({value, onChange}) {
  const opts = [
    { val:'efectivo',      label:'💵 Efectivo',      color:C.verde },
    { val:'transferencia', label:'📱 Transferencia',  color:C.azul  },
  ];
  return (
    <div style={{display:'flex',gap:8,marginBottom:10}}>
      {opts.map(o=>(
        <button key={o.val} onClick={()=>onChange(o.val)} style={{
          flex:1, padding:'10px 8px', borderRadius:10, border:`2px solid ${value===o.val?o.color:C.borde}`,
          background:value===o.val?o.color+'18':C.blanco, color:value===o.val?o.color:C.textoSub,
          fontWeight:value===o.val?800:500, fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', gap:6,
        }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── MODAL CAMBIAR PASSWORD ───────────────────────────────────────────────────
function ModalCambiarPassword({onClose,showToast}) {
  const [form,setForm]=useState({actual:'',nueva:'',confirmar:''});
  const [loading,setLoading]=useState(false);
  const guardar=async()=>{
    if(!form.actual||!form.nueva||!form.confirmar) return showToast('Completar todos los campos','error');
    if(form.nueva.length<6) return showToast('Mínimo 6 caracteres','error');
    if(form.nueva!==form.confirmar) return showToast('Las contraseñas no coinciden','error');
    setLoading(true);
    const res=await api.patch('/auth/cambiar-password',{passwordActual:form.actual,passwordNueva:form.nueva});
    setLoading(false);
    if(res.ok){showToast('Contraseña cambiada');onClose();}
    else showToast(res.error||'Error','error');
  };
  return (
    <Modal title="🔐 Cambiar Contraseña" onClose={onClose}>
      <span style={labelSt}>CONTRASEÑA ACTUAL</span>
      <input style={inputSt} type="password" value={form.actual} onChange={e=>setForm(f=>({...f,actual:e.target.value}))} placeholder="••••••••"/>
      <span style={labelSt}>NUEVA CONTRASEÑA</span>
      <input style={inputSt} type="password" value={form.nueva} onChange={e=>setForm(f=>({...f,nueva:e.target.value}))} placeholder="Mínimo 6 caracteres"/>
      <span style={labelSt}>CONFIRMAR</span>
      <input style={inputSt} type="password" value={form.confirmar} onChange={e=>setForm(f=>({...f,confirmar:e.target.value}))} placeholder="Repetir nueva contraseña"/>
      {form.nueva&&form.confirmar&&form.nueva!==form.confirmar&&<div style={{color:C.error,fontSize:12,marginBottom:8,padding:'6px 10px',background:C.errorBg,borderRadius:8}}>No coinciden</div>}
      <Btn onClick={guardar} disabled={loading}>{loading?'Guardando...':'Cambiar Contraseña'}</Btn>
    </Modal>
  );
}

// ─── MODAL NUEVO PRODUCTO ─────────────────────────────────────────────────────
function ModalNuevoProducto({onClose,showToast,onCreated}) {
  const [form,setForm]=useState({productonombre:'',productotipo:'entero',preciovta:'',preciocosto:''});
  const [loading,setLoading]=useState(false);
  const ganancia=form.preciovta&&form.preciocosto&&+form.preciocosto>0?(((+form.preciovta-+form.preciocosto)/+form.preciocosto)*100).toFixed(1):null;
  const guardar=async()=>{
    if(!form.productonombre) return showToast('Ingresar nombre','error');
    setLoading(true);
    const prod=await api.post('/productos',{productonombre:form.productonombre,productotipo:form.productotipo});
    if(prod._id&&form.preciovta&&form.preciocosto)
      await api.post('/precios',{productoid:prod._id,preciovta:+form.preciovta,preciocosto:+form.preciocosto});
    setLoading(false);
    if(prod._id){showToast(`✅ "${form.productonombre}" creado`);onCreated();onClose();}
    else showToast(prod.error||'Error','error');
  };
  return (
    <Modal title="🥩 Nuevo Producto" onClose={onClose}>
      <span style={labelSt}>NOMBRE</span>
      <input style={inputSt} value={form.productonombre} onChange={e=>setForm(f=>({...f,productonombre:e.target.value}))} placeholder="Ej: Pechuga de pollo" autoFocus/>
      <span style={labelSt}>TIPO</span>
      <select style={inputSt} value={form.productotipo} onChange={e=>setForm(f=>({...f,productotipo:e.target.value}))}>
        {['entero','trozo','elaborado','menudencia','otro'].map(t=><option key={t} value={t}>{t}</option>)}
      </select>
      <div style={{background:C.amarilloBg,borderRadius:10,padding:'12px 14px',marginBottom:12,border:`1px solid ${C.bordeOsc}44`}}>
        <div style={{fontSize:12,color:C.rojo,fontWeight:700,marginBottom:10}}>PRECIO (opcional)</div>
        <span style={labelSt}>PRECIO DE VENTA ($)</span>
        <input style={inputSt} type="number" value={form.preciovta} onChange={e=>setForm(f=>({...f,preciovta:e.target.value}))} placeholder="0"/>
        <span style={labelSt}>COSTO ($)</span>
        <input style={{...inputSt,marginBottom:0}} type="number" value={form.preciocosto} onChange={e=>setForm(f=>({...f,preciocosto:e.target.value}))} placeholder="0"/>
        {ganancia!==null&&<div style={{marginTop:8,color:C.verde,fontWeight:800,fontSize:13}}>Ganancia estimada: {ganancia}%</div>}
      </div>
      <Btn onClick={guardar} disabled={loading}>{loading?'Guardando...':'✓ Crear Producto'}</Btn>
    </Modal>
  );
}

// ─── MODAL APERTURA DE CAJA ───────────────────────────────────────────────────
function ModalAperturaCaja({onClose,showToast,onAbierta}) {
  const [efectivo,setEfectivo]=useState('');
  const [transferencia,setTransferencia]=useState('');
  const [obs,setObs]=useState('');
  const [loading,setLoading]=useState(false);
  const total=(+efectivo||0)+(+transferencia||0);
  const abrir=async()=>{
    setLoading(true);
    const res=await api.post('/caja/apertura',{
      saldoInicial: total,
      efectivoInicial: +efectivo||0,
      transferenciaInicial: +transferencia||0,
      observaciones: obs
    });
    setLoading(false);
    if(res._id||res.apertura){showToast('✅ Caja abierta');onAbierta();onClose();}
    else showToast(res.error||'Error al abrir caja','error');
  };
  return (
    <Modal title="💰 Abrir Caja del Día" onClose={onClose}>
      <div style={{background:C.amarilloBg,borderRadius:12,padding:14,marginBottom:16,border:`1px solid ${C.bordeOsc}44`}}>
        <div style={{fontSize:13,color:C.rojo,fontWeight:700,marginBottom:2}}>Saldo con el que abrís la caja hoy</div>
        <div style={{fontSize:12,color:C.textoSub}}>Ingresá el dinero disponible separado por medio</div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:4}}>
        <div>
          <span style={labelSt}>💵 EFECTIVO ($)</span>
          <input style={inputSt} type="number" value={efectivo} onChange={e=>setEfectivo(e.target.value)} placeholder="0" autoFocus/>
        </div>
        <div>
          <span style={labelSt}>📱 TRANSFERENCIA ($)</span>
          <input style={inputSt} type="number" value={transferencia} onChange={e=>setTransferencia(e.target.value)} placeholder="0"/>
        </div>
      </div>
      {total>0&&<div style={{background:C.verdeBg,border:`1px solid ${C.verde}44`,borderRadius:8,padding:'8px 14px',marginBottom:12,fontWeight:800,color:C.verde}}>Total inicial: {new Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS',maximumFractionDigits:0}).format(total)}</div>}
      <span style={labelSt}>OBSERVACIONES (opcional)</span>
      <input style={inputSt} value={obs} onChange={e=>setObs(e.target.value)} placeholder="Ej: Apertura normal"/>
      <Btn onClick={abrir} disabled={loading} color={C.verde}>{loading?'Abriendo...':'💰 Abrir Caja'}</Btn>
    </Modal>
  );
}

// ─── MODAL CIERRE DE CAJA ─────────────────────────────────────────────────────
function ModalCierreCaja({apertura,datos,onClose,showToast,onCerrada}) {
  const [obs,setObs]=useState('');
  const [loading,setLoading]=useState(false);
  const cerrar=async()=>{
    setLoading(true);
    const res=await api.patch(`/caja/apertura/${apertura._id}/cerrar`,{observaciones:obs});
    setLoading(false);
    if(res._id){showToast('✅ Caja cerrada');onCerrada();onClose();}
    else showToast(res.error||'Error','error');
  };
  return (
    <Modal title="🔒 Cerrar Caja" onClose={onClose}>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
        {[
          {label:'SALDO INICIAL',  value:fmt(apertura.saldoInicial), color:C.textoSub},
          {label:'INGRESOS',       value:fmt(datos.ingresos),         color:C.verde},
          {label:'EGRESOS',        value:fmt(datos.egresos),          color:C.error},
          {label:'SALDO FINAL',    value:fmt(datos.saldoActual),      color:C.rojo},
          {label:'💵 EFECTIVO FINAL',      value:fmt(datos.saldoEfectivo),      color:C.verde},
          {label:'📱 TRANSF. FINAL',       value:fmt(datos.saldoTransferencia), color:C.azul},
        ].map(s=>(
          <div key={s.label} style={{background:C.crema,borderRadius:10,padding:12,border:`1px solid ${C.borde}`}}>
            <div style={{fontSize:10,color:C.textoSub,fontWeight:700}}>{s.label}</div>
            <div style={{fontSize:18,fontWeight:900,color:s.color,marginTop:4}}>{s.value}</div>
          </div>
        ))}
      </div>
      <span style={labelSt}>OBSERVACIONES (opcional)</span>
      <input style={inputSt} value={obs} onChange={e=>setObs(e.target.value)} placeholder="Ej: Cierre sin novedades"/>
      <Btn onClick={cerrar} disabled={loading} color={C.error}>{loading?'Cerrando...':'🔒 Confirmar Cierre'}</Btn>
    </Modal>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────

// ─── MODAL CONFIGURACIÓN REDONDEO ────────────────────────────────────────────
function ModalConfigRedondeo({onClose,showToast}) {
  const [unidad,setUnidad]=useState(getRedondeo());
  const opciones=[1,10,50,100,500];

  const guardar=()=>{
    localStorage.setItem('redondeo',unidad);
    showToast(`✅ Redondeo configurado: $${unidad}`);
    onClose();
  };

  const ejemplo=(u)=>{
    const calculado=7530;
    return redondear(calculado,u);
  };

  return (
    <Modal title="⚙️ Configurar Redondeo" onClose={onClose}>
      <div style={{background:C.amarilloBg,borderRadius:12,padding:14,marginBottom:16,border:`1px solid ${C.bordeOsc}44`}}>
        <div style={{fontSize:13,color:C.rojo,fontWeight:700,marginBottom:4}}>¿A qué múltiplo redondear el precio final?</div>
        <div style={{fontSize:12,color:C.textoSub}}>Ejemplo: $7.530 calculado se cobra...</div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:20}}>
        {opciones.map(o=>(
          <button key={o} onClick={()=>setUnidad(o)} style={{
            background:unidad===o?C.rojo:C.blanco,
            color:unidad===o?'#fff':C.textoOsc,
            border:`2px solid ${unidad===o?C.rojo:C.borde}`,
            borderRadius:12,padding:'12px 8px',
            fontWeight:800,fontSize:15,cursor:'pointer',
            boxShadow:unidad===o?`0 2px 8px ${C.rojo}44`:'none'
          }}>
            {o===1?'Sin redondeo':`$${o}`}
            <div style={{fontSize:11,fontWeight:500,marginTop:4,color:unidad===o?'#fffa':C.textoSub}}>
              {o===1?'$7.530':'→ ' + new Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS',maximumFractionDigits:0}).format(ejemplo(o))}
            </div>
          </button>
        ))}
      </div>
      <div style={{background:C.verdeBg,borderRadius:10,padding:'10px 14px',marginBottom:16,border:`1px solid ${C.verde}44`}}>
        <span style={{color:C.verde,fontWeight:700,fontSize:14}}>
          Con redondeo ${unidad}: $7.530 → {new Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS',maximumFractionDigits:0}).format(ejemplo(unidad))}
        </span>
      </div>
      <Btn onClick={guardar} color={C.rojo}>Guardar configuración</Btn>
    </Modal>
  );
}


// ─── PANTALLA CAJA CERRADA / SIN CAJA ────────────────────────────────────────
function CajaCerradaScreen({cajaData,onAbrir,onReabrir,showToast}) {
  const [loading,setLoading]=useState(false);
  // cajaData puede ser null (sin apertura hoy) o tener apertura cerrada
  const aperturaCerrada=cajaData?.apertura?.estado==='cerrada'?cajaData.apertura:null;

  const reabrir=async()=>{
    if(!aperturaCerrada) return;
    setLoading(true);
    const res=await api.patch(`/caja/apertura/${aperturaCerrada._id}/reabrir`,{});
    setLoading(false);
    if(res._id){showToast('✅ Caja reabierta');onReabrir();}
    else showToast(res.error||'Error al reabrir','error');
  };

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:C.crema,gap:16,padding:32}}>
      <div style={{fontSize:64}}>💰</div>
      {aperturaCerrada?(
        <>
          <div style={{fontWeight:900,fontSize:20,color:C.rojo}}>Caja cerrada</div>
          <div style={{color:C.textoSub,textAlign:'center',maxWidth:320}}>
            La caja de hoy fue cerrada.<br/>
            Saldo al cierre: <b>{new Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS',maximumFractionDigits:0}).format(aperturaCerrada.saldoCierre||0)}</b>
          </div>
          <div style={{display:'flex',gap:12,flexWrap:'wrap',justifyContent:'center'}}>
            <BtnSm onClick={reabrir} color={C.amarilloOsc} outline={false}>
              {loading?'Reabriendo...':'🔓 Reabrir Caja'}
            </BtnSm>
            <BtnSm onClick={onAbrir} color={C.verde}>💰 Nueva Apertura</BtnSm>
          </div>
          <div style={{fontSize:12,color:C.textoSub,textAlign:'center',maxWidth:300,marginTop:8}}>
            "Reabrir" restaura la misma apertura. "Nueva Apertura" crea una nueva con saldo inicial.
          </div>
        </>
      ):(
        <>
          <div style={{fontWeight:900,fontSize:20,color:C.rojo}}>Sin caja abierta hoy</div>
          <div style={{color:C.textoSub,marginBottom:8}}>Abrí la caja para comenzar a operar</div>
          <BtnSm onClick={onAbrir} color={C.verde}>💰 Abrir Caja del Día</BtnSm>
        </>
      )}
    </div>
  );
}

function Login({onLogin}) {
  const [form,setForm]=useState({username:'',password:''});
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState('');
  const submit=async()=>{
    setLoading(true);setErr('');
    try{
      const res=await fetch(`${API_BASE}/auth/login`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(form)});
      const data=await res.json();
      if(data.token){localStorage.setItem('token',data.token);localStorage.setItem('userName',data.nombre);localStorage.setItem('userName_raw',form.username);onLogin(data);}
      else setErr(data.error||'Credenciales incorrectas');
    }catch{setErr('Sin conexión al servidor');}
    setLoading(false);
  };
  return (
    <div style={{background:C.amarillo,minHeight:'100%',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:24}}>
      <div style={{textAlign:'center',marginBottom:32}}>
        <div style={{fontSize:72}}>🐓</div>
        <div style={{fontSize:36,fontWeight:900,color:C.rojo,letterSpacing:-1}}>De Pollo</div>
        <div style={{fontSize:14,color:C.rojo+'AA',fontStyle:'italic',marginTop:2}}>y algo más...</div>
      </div>
      <div style={{background:C.blanco,borderRadius:20,padding:32,width:'100%',maxWidth:380,boxShadow:C.sombraF,border:`3px solid ${C.rojo}`}}>
        <div style={{fontWeight:900,fontSize:18,color:C.rojo,marginBottom:20,textAlign:'center'}}>Ingresar al sistema</div>
        <span style={labelSt}>USUARIO</span>
        <input style={inputSt} value={form.username} onChange={e=>setForm(f=>({...f,username:e.target.value}))} placeholder="admin" onKeyDown={e=>e.key==='Enter'&&submit()} autoComplete="username"/>
        <span style={labelSt}>CONTRASEÑA</span>
        <input style={inputSt} type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} onKeyDown={e=>e.key==='Enter'&&submit()} autoComplete="current-password"/>
        {err&&<div style={{color:C.error,fontSize:13,marginBottom:10,padding:'8px 12px',background:C.errorBg,borderRadius:8}}>{err}</div>}
        <Btn onClick={submit} disabled={loading}>{loading?'Ingresando...':'Ingresar'}</Btn>
      </div>
    </div>
  );
}

// ─── HEADER ───────────────────────────────────────────────────────────────────
function Header({userName,onLogout,onCambiarPassword,onConfigRedondeo,cajaAbierta}) {
  const [menuOpen,setMenuOpen]=useState(false);
  return (
    <div style={{background:C.amarillo,padding:'10px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0,borderBottom:`4px solid ${C.rojo}`,boxShadow:'0 3px 10px #0002',position:'relative'}}>
      <div style={{display:'flex',alignItems:'center',gap:10}}>
        <span style={{fontSize:26}}>🐓</span>
        <div>
          <div style={{fontWeight:900,fontSize:18,color:C.rojo,lineHeight:1}}>De Pollo</div>
          <div style={{fontSize:10,color:C.rojo+'99',fontStyle:'italic'}}>y algo más...</div>
        </div>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        {/* Indicador estado caja */}
        <div style={{background:cajaAbierta?C.verdeBg:C.errorBg,border:`1.5px solid ${cajaAbierta?C.verde:C.error}`,borderRadius:20,padding:'4px 12px',fontSize:12,fontWeight:700,color:cajaAbierta?C.verde:C.error}}>
          {cajaAbierta?'💰 Caja abierta':'🔒 Sin caja'}
        </div>
        <div style={{position:'relative'}}>
          <button onClick={()=>setMenuOpen(m=>!m)} style={{background:C.rojo,color:'#fff',border:'none',borderRadius:20,padding:'7px 14px',fontWeight:700,fontSize:13,display:'flex',alignItems:'center',gap:6}}>
            👤 {userName} ▾
          </button>
          {menuOpen&&(
            <>
              <div style={{position:'fixed',inset:0,zIndex:98}} onClick={()=>setMenuOpen(false)}/>
              <div style={{position:'absolute',right:0,top:'110%',background:C.blanco,borderRadius:12,boxShadow:C.sombraF,border:`1px solid ${C.borde}`,minWidth:200,zIndex:99,overflow:'hidden'}}>
                <button onClick={()=>{setMenuOpen(false);onCambiarPassword();}} style={{width:'100%',padding:'13px 18px',background:'none',border:'none',textAlign:'left',fontSize:14,fontWeight:600,color:C.textoOsc,borderBottom:`1px solid ${C.borde}`}}>🔐 Cambiar contraseña</button>
                <button onClick={()=>{setMenuOpen(false);onConfigRedondeo();}} style={{width:'100%',padding:'13px 18px',background:'none',border:'none',textAlign:'left',fontSize:14,fontWeight:600,color:C.textoOsc,borderBottom:`1px solid ${C.borde}`}}>⚙️ Redondeo de precios <span style={{fontSize:11,color:C.textoSub}}>($${getRedondeo()})</span></button>
                <button onClick={()=>{setMenuOpen(false);onLogout();}} style={{width:'100%',padding:'13px 18px',background:'none',border:'none',textAlign:'left',fontSize:14,fontWeight:600,color:C.error}}>🚪 Cerrar sesión</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── NAV BAR ──────────────────────────────────────────────────────────────────
const NAV=[{key:'ventas',label:'Ventas',icon:'🛒'},{key:'compras',label:'Compras',icon:'📦'},{key:'caja',label:'Caja',icon:'💰'},{key:'reportes',label:'Reportes',icon:'📊'}];
function NavBar({active,onChange}) {
  return (
    <div style={{background:C.blanco,borderTop:`3px solid ${C.amarillo}`,display:'flex',flexShrink:0,boxShadow:'0 -2px 8px #0002',zIndex:50}}>
      {NAV.map(n=>(
        <button key={n.key} onClick={()=>onChange(n.key)} style={{flex:1,padding:'10px 0 8px',background:active===n.key?C.amarilloBg:'none',border:'none',color:active===n.key?C.rojo:C.textoSub,borderTop:active===n.key?`3px solid ${C.rojo}`:'3px solid transparent',fontWeight:active===n.key?800:500,fontSize:11,display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
          <span style={{fontSize:20}}>{n.icon}</span>{n.label}
        </button>
      ))}
    </div>
  );
}

// ─── VENTAS ───────────────────────────────────────────────────────────────────

// Fecha de hoy en Argentina para el input date (YYYY-MM-DD)
function fechaHoyAR() {
  const now = new Date();
  // Ajustar a UTC-3 (Argentina)
  const ar = new Date(now.getTime() - 3 * 60 * 60 * 1000);
  return ar.toISOString().split('T')[0];
}
function VentasTab({onNuevoProducto,cajaAbierta}) {
  const [productos,setProductos]=useState([]);
  const [carrito,setCarrito]=useState([]);
  const [ventas,setVentas]=useState([]);
  const [tab,setTab]=useState('nueva');
  const [loading,setLoading]=useState(false);
  const [obs,setObs]=useState('');
  const [mediodepago,setMediodepago]=useState('efectivo');
  const [toast,setToast]=useState(null);
  const [filtroTipo,setFiltroTipo]=useState('todos');
  const [fechaFiltro,setFechaFiltro]=useState(fechaHoyAR()); // default: hoy
  const showToast=(msg,type='ok')=>setToast({msg,type});

  const loadVentas=useCallback((fecha)=>{
    // fecha vacía ("Todas") no envía filtro de fecha; con fecha, filtra ese día
    const params=fecha?`&desde=${fecha}&hasta=${fecha}`:'';
    api.get(`/ventas?limit=200${params}`).then(d=>setVentas(Array.isArray(d?.ventas)?d.ventas.sort((a,b)=>new Date(b.fecha)-new Date(a.fecha)):[]));
  },[]);

  const loadData=useCallback(()=>{
    api.get('/productos/con-precio').then(d=>setProductos(Array.isArray(d)?d:[]));
    loadVentas(fechaFiltro);
  },[loadVentas,fechaFiltro]);
  useEffect(()=>{loadData();},[loadData]);

  // Recargar ventas cuando cambia el filtro de fecha
  useEffect(()=>{ loadVentas(fechaFiltro); },[fechaFiltro,loadVentas]);

  const tipos=['todos',...new Set(productos.map(p=>p.categoriaid?.slug||p.productotipo||'otro').filter(Boolean))];
  const prodFiltrados=filtroTipo==='todos'?productos:productos.filter(p=>(p.categoriaid?.slug||p.productotipo)===filtroTipo);

  const agregarAlCarrito=(prod)=>{
    if(!prod.precio) return showToast('Sin precio vigente','error');
    const unidad=getRedondeo();
    setCarrito(c=>{
      const ex=c.find(i=>i.productoid===prod._id);
      if(ex){
        const nuevaCant=+(ex.cantidad+0.1).toFixed(1);
        const calculado=+(ex.preciovta*nuevaCant).toFixed(2);
        const redondeado=redondear(calculado,unidad);
        return c.map(i=>i.productoid===prod._id?{...i,cantidad:nuevaCant,totalCalculado:calculado,precioFinal:redondeado,total:redondeado,modificado:redondeado!==calculado}:i);
      }
      const calculado=prod.precio.preciovta*1;
      const redondeado=redondear(calculado,unidad);
      return [...c,{
        productoid:prod._id,productonombre:prod.productonombre,
        precioid:prod.precio._id,preciovta:prod.precio.preciovta,
        cantidad:1,
        totalCalculado:calculado,
        precioFinal:redondeado,
        total:redondeado,
        modificado:redondeado!==calculado
      }];
    });
  };

  const setCantidad=(pid,val)=>{
    const n=parseFloat(val);
    if(isNaN(n)||n<0) return;
    if(n===0) return setCarrito(c=>c.filter(i=>i.productoid!==pid));
    const unidad=getRedondeo();
    setCarrito(c=>c.map(i=>{
      if(i.productoid!==pid) return i;
      const calculado=+(n*i.preciovta).toFixed(2);
      const redondeado=redondear(calculado,unidad);
      return {...i,cantidad:n,totalCalculado:calculado,precioFinal:redondeado,total:redondeado,modificado:redondeado!==calculado};
    }));
  };

  const setPrecioFinal=(pid,val)=>{
    const n=parseFloat(val);
    if(isNaN(n)||n<0) return;
    setCarrito(c=>c.map(i=>i.productoid===pid?{...i,precioFinal:n,total:n,modificado:n!==i.totalCalculado}:i));
  };

  const ajustarPrecio=(pid,delta)=>{
    setCarrito(c=>c.map(i=>{
      if(i.productoid!==pid) return i;
      const nuevo=Math.max(0,(i.precioFinal||i.total)+delta);
      return {...i,precioFinal:nuevo,total:nuevo,modificado:nuevo!==i.totalCalculado};
    }));
  };

  const totalCarrito=carrito.reduce((s,i)=>s+(i.precioFinal||i.total),0);

  const confirmarVenta=async()=>{
    if(!carrito.length) return showToast('Carrito vacío','error');
    if(!cajaAbierta) return showToast('⚠️ Abrí la caja antes de registrar ventas','error');
    setLoading(true);
    const res=await api.post('/ventas',{detalles:carrito,observaciones:obs,mediodepago});
    setLoading(false);
    if(res._id){showToast('✅ Venta registrada');setCarrito([]);setObs('');setMediodepago('efectivo');loadData();}
    else showToast(res.error||'Error','error');
  };

  const mpColor={'efectivo':C.verde,'transferencia':C.azul};

  return (
    <div style={{display:'flex',flexDirection:'column',flex:1,overflow:'hidden',minHeight:0}}>
      {toast&&<Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
      <SubTab tabs={[['nueva','🛒 Nueva Venta'],['historial','📋 Historial']]} active={tab} onChange={setTab}/>

      {tab==='nueva'&&(
        <div style={{display:'flex',flex:1,overflow:'hidden',minHeight:0}}>
          {/* Panel productos */}
          <div style={{flex:1,overflowY:'auto',padding:'14px 14px',background:C.crema,minWidth:0}}>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:14}}>
              {tipos.map(t=>(
                <button key={t} onClick={()=>setFiltroTipo(t)} style={{background:filtroTipo===t?C.rojo:C.blanco,color:filtroTipo===t?'#fff':C.rojo,border:`2px solid ${C.rojo}`,borderRadius:20,padding:'6px 18px',fontSize:12,fontWeight:800,textTransform:'uppercase',boxShadow:filtroTipo===t?`0 2px 8px ${C.rojo}44`:'none'}}>
                  {t==='todos'?'TODO':t}
                </button>
              ))}
              <button onClick={onNuevoProducto} style={{background:C.blanco,color:C.rojo,border:`2px dashed ${C.rojo}`,borderRadius:20,padding:'6px 18px',fontSize:12,fontWeight:700}}>+ Nuevo</button>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
              <span style={{fontWeight:900,fontSize:15,color:C.rojo,whiteSpace:'nowrap'}}>{filtroTipo==='todos'?'TODOS LOS PRODUCTOS':filtroTipo.toUpperCase()}</span>
              <div style={{flex:1,borderBottom:`2px dashed ${C.bordeOsc}`}}/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:10}}>
              {prodFiltrados.map(p=>{
                const enCarrito=carrito.find(i=>i.productoid===p._id);
                return (
                  <button key={p._id} onClick={()=>agregarAlCarrito(p)} style={{background:C.blanco,border:`2px solid ${enCarrito?C.rojo:C.borde}`,borderRadius:14,padding:'14px 12px',textAlign:'left',boxShadow:enCarrito?`0 4px 14px ${C.rojo}33`:C.sombra,cursor:p.precio?'pointer':'not-allowed',opacity:p.precio?1:0.55,position:'relative'}}>
                    {enCarrito&&<div style={{position:'absolute',top:8,right:8,background:C.rojo,color:'#fff',borderRadius:'50%',width:22,height:22,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:900}}>{enCarrito.cantidad}</div>}
                    <div style={{width:16,height:16,borderRadius:'50%',border:`2px solid ${enCarrito?C.rojo:C.bordeOsc}`,background:enCarrito?C.rojo:'transparent',marginBottom:8}}/>
                    <div style={{fontWeight:800,fontSize:13,color:C.textoOsc,marginBottom:4,lineHeight:1.2}}>{p.productonombre}</div>
                    <div style={{fontSize:10,color:C.textoSub,marginBottom:8,fontWeight:600}}>POR {(p.categoriaid?.nombre||p.productotipo||'').toUpperCase()}</div>
                    {p.precio?<PrecioChip value={fmt(p.precio.preciovta)}/>:<span style={{fontSize:12,color:C.error,fontWeight:700}}>Sin precio</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Carrito */}
          <div style={{width:310,minWidth:260,background:C.blanco,display:'flex',flexDirection:'column',borderLeft:`2px solid ${C.borde}`,flexShrink:0}}>
            <div style={{padding:'12px 14px',background:C.amarillo,borderBottom:`2px solid ${C.rojo}`,flexShrink:0}}>
              <span style={{fontWeight:900,fontSize:15,color:C.rojo}}>🛒 Carrito</span>
              {carrito.length>0&&<span style={{marginLeft:8,background:C.rojo,color:'#fff',borderRadius:'50%',padding:'1px 7px',fontSize:12,fontWeight:900}}>{carrito.length}</span>}
            </div>
            <div style={{flex:1,overflowY:'auto',padding:'10px 12px',minHeight:0}}>
              {carrito.length===0
                ?<div style={{textAlign:'center',color:C.textoSub,paddingTop:40}}><div style={{fontSize:36,marginBottom:8}}>🛒</div>Tocá un producto</div>
                :carrito.map(item=>(
                  <div key={item.productoid} style={{background:C.crema,borderRadius:10,padding:'10px 11px',marginBottom:8,border:`1.5px solid ${C.borde}`}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                      <div style={{fontWeight:700,fontSize:13,color:C.textoOsc,flex:1,lineHeight:1.2}}>{item.productonombre}</div>
                      <button onClick={()=>setCarrito(c=>c.filter(i=>i.productoid!==item.productoid))} style={{background:'none',border:'none',color:C.error,fontSize:18,lineHeight:1}}>×</button>
                    </div>
                    {/* Cantidad */}
                    <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:6}}>
                      <button onClick={()=>setCantidad(item.productoid,+(item.cantidad-0.1).toFixed(1))} style={{background:C.blanco,border:`1.5px solid ${C.borde}`,borderRadius:6,color:C.rojo,width:28,height:28,fontSize:16,fontWeight:900}}>−</button>
                      <input type="number" value={item.cantidad} min="0.01" step="0.1" onChange={e=>setCantidad(item.productoid,e.target.value)} style={{width:52,textAlign:'center',background:C.blanco,border:`1.5px solid ${C.borde}`,borderRadius:6,color:C.textoOsc,padding:'4px',fontSize:14,fontWeight:700}}/>
                      <button onClick={()=>setCantidad(item.productoid,+(item.cantidad+0.1).toFixed(1))} style={{background:C.blanco,border:`1.5px solid ${C.borde}`,borderRadius:6,color:C.rojo,width:28,height:28,fontSize:16,fontWeight:900}}>+</button>
                      <span style={{fontSize:11,color:C.textoSub,marginLeft:4}}>{fmt(item.preciovta)}/u</span>
                    </div>
                    {/* Precio final con redondeo */}
                    <div style={{background:item.modificado?C.amarilloBg:C.crema,borderRadius:8,padding:'6px 8px',border:`1.5px solid ${item.modificado?C.amarilloOsc:C.borde}`}}>
                      <div style={{fontSize:9,color:item.modificado?C.amarilloOsc:C.textoSub,fontWeight:700,letterSpacing:1,marginBottom:3}}>
                        {item.modificado?`CALCULADO: ${fmt(item.totalCalculado)} → REDONDEADO`:'PRECIO FINAL'}
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:4}}>
                        <button onClick={()=>ajustarPrecio(item.productoid,-100)} style={{background:C.blanco,border:`1px solid ${C.borde}`,borderRadius:5,color:C.rojo,width:26,height:26,fontSize:13,fontWeight:900,lineHeight:1}}>−</button>
                        <input type="number" value={item.precioFinal||item.total} onChange={e=>setPrecioFinal(item.productoid,+e.target.value)}
                          style={{flex:1,textAlign:'center',background:'transparent',border:'none',color:C.rojo,fontSize:16,fontWeight:900,minWidth:0}}/>
                        <button onClick={()=>ajustarPrecio(item.productoid,100)} style={{background:C.blanco,border:`1px solid ${C.borde}`,borderRadius:5,color:C.verde,width:26,height:26,fontSize:13,fontWeight:900,lineHeight:1}}>+</button>
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
            {carrito.length>0&&(
              <div style={{padding:'12px',borderTop:`2px solid ${C.borde}`,background:C.crema,flexShrink:0}}>
                {/* Medio de pago */}
                <span style={labelSt}>MEDIO DE PAGO</span>
                <RadioPago value={mediodepago} onChange={setMediodepago}/>
                <input style={{...inputSt,marginBottom:8,fontSize:13}} placeholder="Observaciones..." value={obs} onChange={e=>setObs(e.target.value)}/>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8,padding:'8px 12px',background:C.amarillo,borderRadius:10}}>
                  <span style={{fontWeight:900,fontSize:14,color:C.rojo}}>TOTAL</span>
                  <span style={{fontSize:20,fontWeight:900,color:C.rojo}}>{fmt(totalCarrito)}</span>
                </div>
                {!cajaAbierta&&<div style={{background:C.errorBg,border:`1px solid ${C.error}44`,borderRadius:8,padding:'8px 12px',marginBottom:8,fontSize:12,color:C.error,fontWeight:700}}>⚠️ Abrí la caja primero</div>}
                <Btn onClick={confirmarVenta} disabled={loading||!cajaAbierta}>{loading?'Registrando...':'✓ Confirmar Venta'}</Btn>
                <button onClick={()=>setCarrito([])} style={{width:'100%',background:'none',border:`1px solid ${C.borde}`,borderRadius:10,padding:'7px',color:C.textoSub,marginTop:6,fontSize:13}}>🗑 Vaciar</button>
              </div>
            )}
          </div>
        </div>
      )}

      {tab==='historial'&&(
        <div style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column',minHeight:0}}>
          {/* Filtro por fecha */}
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:C.blanco,borderBottom:`1px solid ${C.borde}`,flexShrink:0,flexWrap:'wrap'}}>
            <span style={{fontSize:12,color:C.textoSub,fontWeight:700}}>📅 Fecha:</span>
            <input type="date" value={fechaFiltro} onChange={e=>setFechaFiltro(e.target.value)}
              style={{border:`1.5px solid ${C.borde}`,borderRadius:8,padding:'6px 10px',fontSize:13,color:C.textoOsc,background:C.blanco}}/>
            <button onClick={()=>setFechaFiltro(fechaHoyAR())}
              style={{background:C.amarillo,color:C.rojo,border:'none',borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:800,cursor:'pointer'}}>
              Hoy
            </button>
            <button onClick={()=>setFechaFiltro('')}
              style={{background:C.blanco,color:C.textoSub,border:`1px solid ${C.borde}`,borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:600,cursor:'pointer'}}>
              Todas
            </button>
            {ventas.length>0&&(
              <span style={{marginLeft:'auto',fontSize:13,color:C.textoSub,fontWeight:600}}>
                {ventas.length} venta{ventas.length!==1?'s':''} · <b style={{color:C.verde}}>{fmt(ventas.reduce((s,v)=>s+v.total,0))}</b>
              </span>
            )}
          </div>
          {/* Acumulado por medio de pago — mismo patrón que Compras */}
          <div style={{display:'flex',gap:10,padding:'10px 14px',background:C.blanco,borderBottom:`2px solid ${C.borde}`,flexShrink:0,flexWrap:'wrap'}}>
            <div style={{flex:'1 1 160px',background:C.verdeBg,border:`1px solid ${C.verde}`,borderRadius:10,padding:'8px 14px'}}>
              <div style={{fontSize:11,color:C.verde,fontWeight:800}}>💵 EFECTIVO</div>
              <div style={{fontSize:17,fontWeight:900,color:C.verde}}>{fmt(ventas.filter(v=>v.mediodepago!=='transferencia').reduce((s,v)=>s+v.total,0))}</div>
            </div>
            <div style={{flex:'1 1 160px',background:C.azulBg,border:`1px solid ${C.azul}`,borderRadius:10,padding:'8px 14px'}}>
              <div style={{fontSize:11,color:C.azul,fontWeight:800}}>📱 TRANSFERENCIA</div>
              <div style={{fontSize:17,fontWeight:900,color:C.azul}}>{fmt(ventas.filter(v=>v.mediodepago==='transferencia').reduce((s,v)=>s+v.total,0))}</div>
            </div>
          </div>
          <div style={{overflowY:'auto',flex:1,minHeight:0}}>
            <table>
              <thead style={{position:'sticky',top:0,zIndex:10}}>
                <tr style={{background:C.rojo}}>
                  {['Fecha','Hora','Productos','Pago','Total'].map(h=>(
                    <th key={h} style={{padding:'10px 14px',color:C.amarillo,fontWeight:800,fontSize:12,whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ventas.length===0&&<tr><td colSpan={5} style={{textAlign:'center',padding:40,color:C.textoSub}}>Sin ventas registradas</td></tr>}
                {ventas.map((v,i)=>(
                  <tr key={v._id} style={{background:i%2===0?C.blanco:C.cremaDark,borderBottom:`1px solid ${C.borde}`}}>
                    <td style={{padding:'9px 14px',fontWeight:700,whiteSpace:'nowrap',color:C.textoOsc}}>{fmtDate(v.fecha)}</td>
                    <td style={{padding:'9px 14px',color:C.textoSub,whiteSpace:'nowrap'}}>{fmtTime(v.fecha)}</td>
                    <td style={{padding:'9px 14px'}}>
                      <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                        {v.detalles?.map((d,j)=>(
                          <span key={j} style={{background:C.amarillo,color:C.rojo,borderRadius:5,padding:'1px 8px',fontSize:11,fontWeight:800,whiteSpace:'nowrap'}}>{d.productonombre} ×{d.cantidad}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{padding:'9px 14px',whiteSpace:'nowrap'}}>
                      <span style={{background:v.mediodepago==='transferencia'?C.azulBg:C.verdeBg,color:v.mediodepago==='transferencia'?C.azul:C.verde,borderRadius:6,padding:'2px 8px',fontSize:11,fontWeight:800}}>
                        {v.mediodepago==='transferencia'?'📱 Trans.':'💵 Efect.'}
                      </span>
                    </td>
                    <td style={{padding:'9px 14px',whiteSpace:'nowrap',textAlign:'right'}}>
                      <div style={{fontWeight:900,color:C.verde,fontSize:15}}>{fmt(v.total)}</div>
                      {v.detalles?.some(d=>d.modificado)&&(
                        <div style={{fontSize:10,color:C.amarilloOsc,fontWeight:700}}>
                          calc: {fmt(v.detalles.reduce((s,d)=>s+(d.subtotal||d.totalCalculado||+(d.preciovta*d.cantidad).toFixed(2)),0))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
          

// ─── PRECIOS ──────────────────────────────────────────────────────────────────
function PreciosTab({onNuevoProducto}) {
  const [precios,setPrecios]=useState([]);
  const [productos,setProductos]=useState([]);
  const [modal,setModal]=useState(false);
  const [form,setForm]=useState({productoid:'',preciovta:'',preciocosto:'',publicarWeb:true});
  const [toast,setToast]=useState(null);
  const showToast=(msg,type='ok')=>setToast({msg,type});
  const load=()=>{api.get('/precios/vigentes').then(d=>setPrecios(Array.isArray(d)?d:[]));api.get('/productos').then(d=>setProductos(Array.isArray(d)?d:[]));};
  useEffect(load,[]);
  const ganancia=form.preciovta&&form.preciocosto&&+form.preciocosto>0?(((+form.preciovta-+form.preciocosto)/+form.preciocosto)*100).toFixed(1):null;

  const togglePublicarWeb=async(p)=>{
    const res=await api.put(`/precios/${p._id}`,{publicarWeb:!p.publicarWeb});
    if(res._id){showToast(res.publicarWeb?'✅ Publicado en web':'🚫 Ocultado de la web');load();}
    else showToast(res.error||'Error','error');
  };

  const guardar=async()=>{
    if(!form.productoid||!form.preciovta||!form.preciocosto) return showToast('Completar todos los campos','error');
    const res=await api.post('/precios',{...form,preciovta:+form.preciovta,preciocosto:+form.preciocosto,publicarWeb:form.publicarWeb!==false});
    if(res._id){showToast('Precio guardado');setModal(false);setForm({productoid:'',preciovta:'',preciocosto:'',publicarWeb:true});load();}
    else showToast(res.error||'Error','error');
  };
  return (
    <div style={{overflowY:'auto',padding:16,flex:1,background:C.crema,minHeight:0}}>
      {toast&&<Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:8}}>
        <span style={{fontWeight:900,fontSize:16,color:C.rojo}}>LISTA DE PRECIOS</span>
        <div style={{display:'flex',gap:8}}><BtnSm onClick={onNuevoProducto} outline>+ Producto</BtnSm><BtnSm onClick={()=>setModal(true)}>+ Precio</BtnSm></div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12}}>
        {precios.map(p=>{
          const cat=p.productoid?.categoriaid;
          return(
          <div key={p._id} style={{background:C.blanco,borderRadius:14,padding:16,border:`1.5px solid ${p.publicarWeb?C.borde:C.error+'55'}`,boxShadow:C.sombra,opacity:p.publicarWeb?1:0.75}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
              <span style={{background:cat?.colorWeb||C.rojo,color:'#fff',borderRadius:6,padding:'2px 9px',fontSize:11,fontWeight:800}}>{cat?.nombre||p.productoid?.productotipo||'—'}</span>
              {/* Toggle publicar en web */}
              <button onClick={()=>togglePublicarWeb(p)} style={{background:p.publicarWeb?C.verdeBg:C.errorBg,color:p.publicarWeb?C.verde:C.error,border:`1px solid ${p.publicarWeb?C.verde:C.error}44`,borderRadius:20,padding:'3px 10px',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                {p.publicarWeb?'🌐 Web':'🚫 Oculto'}
              </button>
            </div>
            <div style={{fontWeight:800,fontSize:15,color:C.textoOsc,margin:'8px 0'}}>{p.productoid?.productonombre}</div>
            <PrecioChip value={fmt(p.preciovta)}/>
            <div style={{fontSize:12,color:C.textoSub,marginTop:8}}>Costo: {fmt(p.preciocosto)}</div>
            <div style={{marginTop:6}}><span style={{background:C.verdeBg,color:C.verde,borderRadius:6,padding:'2px 9px',fontSize:11,fontWeight:800}}>+{p.porcentajeganancia}% ganancia</span></div>
          </div>
        );})}
      </div>
      {modal&&(
        <Modal title="Actualizar Precio" onClose={()=>setModal(false)}>
          <span style={labelSt}>PRODUCTO</span>
          <select style={inputSt} value={form.productoid} onChange={e=>setForm(f=>({...f,productoid:e.target.value}))}>
            <option value="">Seleccionar...</option>
            {productos.map(p=><option key={p._id} value={p._id}>{p.productonombre}</option>)}
          </select>
          <span style={labelSt}>PRECIO DE VENTA ($)</span>
          <input style={inputSt} type="number" value={form.preciovta} onChange={e=>setForm(f=>({...f,preciovta:e.target.value}))} placeholder="0"/>
          <span style={labelSt}>COSTO ($)</span>
          <input style={inputSt} type="number" value={form.preciocosto} onChange={e=>setForm(f=>({...f,preciocosto:e.target.value}))} placeholder="0"/>
          {ganancia!==null&&<div style={{marginBottom:12,padding:'10px 14px',background:C.verdeBg,borderRadius:10}}><span style={{color:C.verde,fontWeight:800}}>Ganancia estimada: {ganancia}%</span></div>}
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14,padding:'10px 14px',background:C.amarilloBg,borderRadius:10,border:`1px solid ${C.bordeOsc}44`}}>
            <input type="checkbox" id="pw-check" checked={form.publicarWeb!==false} onChange={e=>setForm(f=>({...f,publicarWeb:e.target.checked}))} style={{width:18,height:18,cursor:'pointer'}}/>
            <label htmlFor="pw-check" style={{fontSize:14,fontWeight:700,color:C.textoOsc,cursor:'pointer'}}>🌐 Publicar en dpollo.com.ar</label>
          </div>
          <Btn onClick={guardar}>Guardar Precio</Btn>
        </Modal>
      )}
    </div>
  );
}

// ─── COMPRAS ──────────────────────────────────────────────────────────────────
function ComprasTab() {
  const [compras,setCompras]=useState([]);
  const [modal,setModal]=useState(false);
  const [form,setForm]=useState({monto:'',proveedor:'',descripcion:'',mediodepago:'efectivo'});
  const [toast,setToast]=useState(null);
  const [fechaFiltro,setFechaFiltro]=useState(fechaHoyAR()); // default: hoy, igual que Ventas
  const showToast=(msg,type='ok')=>setToast({msg,type});

  const loadCompras=useCallback((fecha)=>{
    // fecha vacía ("Todas") no envía filtro de fecha; con fecha, filtra ese día
    const params=fecha?`?desde=${fecha}&hasta=${fecha}`:'';
    api.get(`/compras${params}`).then(d=>setCompras(Array.isArray(d)?d.sort((a,b)=>new Date(b.fecha)-new Date(a.fecha)):[]));
  },[]);
  useEffect(()=>{ loadCompras(fechaFiltro); },[fechaFiltro,loadCompras]);

  const guardar=async()=>{
    if(!form.monto) return showToast('Ingresar monto','error');
    const res=await api.post('/compras',{...form,monto:+form.monto});
    if(res._id){showToast('Compra registrada');setModal(false);setForm({monto:'',proveedor:'',descripcion:''});loadCompras(fechaFiltro);}
    else showToast(res.error||'Error','error');
  };
  const efectivoTotal=compras.reduce((s,c)=>s+(c.mediodepago==='transferencia'?0:c.monto),0);
  const transferenciaTotal=compras.reduce((s,c)=>s+(c.mediodepago==='transferencia'?c.monto:0),0);
  return (
    <div style={{display:'flex',flexDirection:'column',flex:1,overflow:'hidden',minHeight:0,background:C.crema}}>
      {toast&&<Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
      <div style={{padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',background:C.blanco,borderBottom:`2px solid ${C.borde}`,flexShrink:0}}>
        <div>
          <div style={{fontWeight:900,fontSize:15,color:C.rojo}}>COMPRAS A PROVEEDORES</div>
          <div style={{fontSize:13,color:C.textoSub}}>Total: <b style={{color:C.error}}>{fmt(compras.reduce((s,c)=>s+c.monto,0))}</b></div>
        </div>
        <BtnSm onClick={()=>setModal(true)}>+ Nueva compra</BtnSm>
      </div>
      {/* Filtro por fecha — mismo patrón que el Historial de Ventas */}
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:C.blanco,borderBottom:`1px solid ${C.borde}`,flexShrink:0,flexWrap:'wrap'}}>
        <span style={{fontSize:12,color:C.textoSub,fontWeight:700}}>📅 Fecha:</span>
        <input type="date" value={fechaFiltro} onChange={e=>setFechaFiltro(e.target.value)}
          style={{border:`1.5px solid ${C.borde}`,borderRadius:8,padding:'6px 10px',fontSize:13,color:C.textoOsc,background:C.blanco}}/>
        <button onClick={()=>setFechaFiltro(fechaHoyAR())}
          style={{background:C.amarillo,color:C.rojo,border:'none',borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:800,cursor:'pointer'}}>
          Hoy
        </button>
        <button onClick={()=>setFechaFiltro('')}
          style={{background:C.blanco,color:C.textoSub,border:`1px solid ${C.borde}`,borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:600,cursor:'pointer'}}>
          Todas
        </button>
        {compras.length>0&&(
          <span style={{marginLeft:'auto',fontSize:13,color:C.textoSub,fontWeight:600}}>
            {compras.length} compra{compras.length!==1?'s':''} · <b style={{color:C.error}}>{fmt(compras.reduce((s,c)=>s+c.monto,0))}</b>
          </span>
        )}
      </div>
      {/* Acumulado por medio de pago */}
      <div style={{display:'flex',gap:10,padding:'10px 14px',background:C.blanco,borderBottom:`2px solid ${C.borde}`,flexShrink:0,flexWrap:'wrap'}}>
        <div style={{flex:'1 1 160px',background:C.verdeBg,border:`1px solid ${C.verde}`,borderRadius:10,padding:'8px 14px'}}>
          <div style={{fontSize:11,color:C.verde,fontWeight:800}}>💵 EFECTIVO</div>
          <div style={{fontSize:17,fontWeight:900,color:C.verde}}>{fmt(efectivoTotal)}</div>
        </div>
        <div style={{flex:'1 1 160px',background:C.azulBg,border:`1px solid ${C.azul}`,borderRadius:10,padding:'8px 14px'}}>
          <div style={{fontSize:11,color:C.azul,fontWeight:800}}>📱 TRANSFERENCIA</div>
          <div style={{fontSize:17,fontWeight:900,color:C.azul}}>{fmt(transferenciaTotal)}</div>
        </div>
      </div>
      <div style={{overflowY:'auto',flex:1,minHeight:0}}>
        <table>
          <thead style={{position:'sticky',top:0,zIndex:10}}>
            <tr style={{background:C.rojo}}>{['Fecha','Proveedor','Descripción','Pago','Monto'].map(h=><th key={h} style={{padding:'10px 14px',color:C.amarillo,fontWeight:800,fontSize:12}}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {compras.length===0&&<tr><td colSpan={5} style={{textAlign:'center',padding:40,color:C.textoSub}}>{fechaFiltro?'Sin compras registradas ese día':'Sin compras registradas'}</td></tr>}
            {compras.map((c,i)=>(
              <tr key={c._id} style={{background:i%2===0?C.blanco:C.cremaDark,borderBottom:`1px solid ${C.borde}`}}>
                <td style={{padding:'9px 14px',whiteSpace:'nowrap',color:C.textoOsc,fontWeight:600}}>{fmtDate(c.fecha)} {fmtTime(c.fecha)}</td>
                <td style={{padding:'9px 14px',color:C.textoOsc,fontWeight:600}}>{c.proveedor||'—'}</td>
                <td style={{padding:'9px 14px',color:C.textoSub}}>{c.descripcion||'—'}</td>
                <td style={{padding:'9px 14px',whiteSpace:'nowrap'}}>
                  <span style={{background:c.mediodepago==='transferencia'?C.azulBg:C.verdeBg,color:c.mediodepago==='transferencia'?C.azul:C.verde,borderRadius:6,padding:'2px 8px',fontSize:11,fontWeight:800}}>
                    {c.mediodepago==='transferencia'?'📱 Trans.':'💵 Efect.'}
                  </span>
                </td>
                <td style={{padding:'9px 14px',fontWeight:900,color:C.error,textAlign:'right',whiteSpace:'nowrap'}}>{fmt(c.monto)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal&&(
        <Modal title="Nueva Compra" onClose={()=>setModal(false)}>
          <span style={labelSt}>MEDIO DE PAGO</span>
          <RadioPago value={form.mediodepago} onChange={v=>setForm(f=>({...f,mediodepago:v}))}/>
          <span style={labelSt}>MONTO ($)</span>
          <input style={inputSt} type="number" value={form.monto} onChange={e=>setForm(f=>({...f,monto:e.target.value}))} placeholder="0" autoFocus/>
          <span style={labelSt}>PROVEEDOR</span>
          <input style={inputSt} value={form.proveedor} onChange={e=>setForm(f=>({...f,proveedor:e.target.value}))} placeholder="Nombre del proveedor"/>
          <span style={labelSt}>DESCRIPCIÓN</span>
          <input style={inputSt} value={form.descripcion} onChange={e=>setForm(f=>({...f,descripcion:e.target.value}))} placeholder="Detalle de la compra"/>
          <Btn onClick={guardar}>Registrar Compra</Btn>
        </Modal>
      )}
    </div>
  );
}

// ─── CAJA TAB ─────────────────────────────────────────────────────────────────
function CajaTab({cajaData,onRefresh,showToast}) {
  const [modalMov,setModalMov]=useState(false);
  const [modalCierre,setModalCierre]=useState(false);
  const [form,setForm]=useState({tipomovimiento:'ingreso',mediodepago:'efectivo',descmovimiento:'',monto:''});
  const [loading,setLoading]=useState(false);

  const {apertura,ingresos=0,egresos=0,saldoActual=0,movimientos=[]}=cajaData||{};

  const guardarMov=async()=>{
    if(!form.descmovimiento||!form.monto) return showToast('Completar todos los campos','error');
    setLoading(true);
    const res=await api.post('/caja/movimientos',{...form,monto:+form.monto});
    setLoading(false);
    if(res._id){showToast('Movimiento registrado');setModalMov(false);setForm({tipomovimiento:'ingreso',mediodepago:'efectivo',descmovimiento:'',monto:''});onRefresh();}
    else showToast(res.error||'Error','error');
  };

  if(!apertura) return (
    <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',background:C.crema}}>
      <div style={{textAlign:'center',padding:32}}>
        <div style={{fontSize:64,marginBottom:16}}>💰</div>
        <div style={{fontWeight:900,fontSize:20,color:C.rojo,marginBottom:8}}>Sin caja abierta</div>
        <div style={{color:C.textoSub,marginBottom:24}}>No hay caja abierta para hoy.<br/>Abrí la caja para comenzar a operar.</div>
      </div>
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',flex:1,overflow:'hidden',minHeight:0,background:C.crema}}>
      {/* Stats */}
      <div style={{padding:'14px 16px',background:C.blanco,borderBottom:`2px solid ${C.borde}`,flexShrink:0}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
          <div>
            <div style={{fontWeight:900,fontSize:15,color:C.rojo}}>CAJA DEL DÍA — {fmtDate(apertura.fecha)}</div>
            <div style={{fontSize:12,color:C.textoSub}}>Abierta por {apertura.usuario} · {fmtTime(apertura.fechaApertura)}</div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <BtnSm onClick={()=>setModalMov(true)}>+ Movimiento</BtnSm>
            <BtnSm onClick={()=>setModalCierre(true)} color={C.error}>🔒 Cerrar</BtnSm>
          </div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
          {[
            {label:'INICIAL',  value:fmt(apertura.saldoInicial), color:C.textoSub},
            {label:'INGRESOS', value:fmt(ingresos),              color:C.verde},
            {label:'EGRESOS',  value:fmt(egresos),               color:C.error},
            {label:'SALDO',    value:fmt(saldoActual),           color:C.rojo},
          ].map(s=>(
            <div key={s.label} style={{background:C.crema,borderRadius:10,padding:'10px 12px',border:`1.5px solid ${s.color}33`}}>
              <div style={{fontSize:9,color:s.color,fontWeight:800,letterSpacing:1}}>{s.label}</div>
              <div style={{fontSize:16,fontWeight:900,color:s.color,marginTop:3}}>{s.value}</div>
            </div>
          ))}
        </div>
        {/* Desglose por medio de pago */}
        <div style={{display:'flex',gap:10,marginTop:10,flexWrap:'wrap'}}>
          {[
            {label:'💵 Efectivo',      value:fmt(cajaData.saldoEfectivo||0),      color:C.verde},
            {label:'📱 Transferencia', value:fmt(cajaData.saldoTransferencia||0), color:C.azul},
          ].map(s=>(
            <div key={s.label} style={{background:C.blanco,borderRadius:8,padding:'6px 14px',border:`1px solid ${s.color}44`,fontSize:13,fontWeight:700,color:s.color}}>{s.label}: {s.value}</div>
          ))}
          <div style={{background:C.amarilloBg,borderRadius:8,padding:'6px 14px',border:`1px solid ${C.amarilloOsc}44`,fontSize:11,color:C.textoSub,display:'flex',flexDirection:'column'}}>
            <span>💵 Ing.Efect: {fmt(cajaData.ingresosEfectivo||0)} / Egr: {fmt(cajaData.egresosEfectivo||0)}</span>
            <span>📱 Ing.Transf: {fmt(cajaData.ingresosTransferencia||0)} / Egr: {fmt(cajaData.egresosTransferencia||0)}</span>
          </div>
        </div>
      </div>

      {/* Movimientos */}
      <div style={{overflowY:'auto',flex:1,minHeight:0}}>
        <table>
          <thead style={{position:'sticky',top:0,zIndex:10}}>
            <tr style={{background:C.rojo}}>{['Hora','Tipo','M.Pago','Descripción','Monto'].map(h=><th key={h} style={{padding:'9px 14px',color:C.amarillo,fontWeight:800,fontSize:12,whiteSpace:'nowrap'}}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {movimientos.length===0&&<tr><td colSpan={5} style={{textAlign:'center',padding:32,color:C.textoSub}}>Sin movimientos</td></tr>}
            {movimientos.map((m,i)=>{
              const esAnulacion=m.descmovimiento?.toLowerCase().includes('anulaci');
              return (
              <tr key={m._id} style={{background:esAnulacion?'#FFF3CD':i%2===0?C.blanco:C.cremaDark,borderBottom:`1px solid ${C.borde}`,opacity:esAnulacion?0.8:1}}>
                <td style={{padding:'9px 14px',color:C.textoSub,whiteSpace:'nowrap',fontSize:12}}>{fmtTime(m.fecha)}</td>
                <td style={{padding:'9px 14px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                    <span style={{background:m.tipomovimiento==='ingreso'?C.verdeBg:C.errorBg,color:m.tipomovimiento==='ingreso'?C.verde:C.error,borderRadius:6,padding:'2px 8px',fontSize:11,fontWeight:800}}>
                      {m.tipomovimiento==='ingreso'?'↑ Ingreso':'↓ Egreso'}
                    </span>
                    {esAnulacion&&<span style={{background:'#FFC107',color:'#7A5900',borderRadius:6,padding:'1px 7px',fontSize:10,fontWeight:800}}>⚠️ ANULADA</span>}
                  </div>
                </td>
                <td style={{padding:'9px 14px'}}>
                  <span style={{background:m.mediodepago==='transferencia'?C.azulBg:C.verdeBg,color:m.mediodepago==='transferencia'?C.azul:C.verde,borderRadius:6,padding:'2px 8px',fontSize:11,fontWeight:700}}>
                    {m.mediodepago==='transferencia'?'📱':'💵'} {m.mediodepago}
                  </span>
                </td>
                <td style={{padding:'9px 14px',color:esAnulacion?C.textoSub:C.textoOsc,fontSize:13,textDecoration:esAnulacion?'line-through':'none'}}>{m.descmovimiento}</td>
                <td style={{padding:'9px 14px',fontWeight:900,color:m.tipomovimiento==='ingreso'?C.verde:C.error,textAlign:'right',whiteSpace:'nowrap',textDecoration:esAnulacion?'line-through':'none'}}>{m.tipomovimiento==='egreso'?'−':'+' }{fmt(m.monto)}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal movimiento manual */}
      {modalMov&&(
        <Modal title="💰 Movimiento Manual" onClose={()=>setModalMov(false)}>
          <span style={labelSt}>TIPO</span>
          <div style={{display:'flex',gap:8,marginBottom:12}}>
            {[['ingreso','↑ Ingreso',C.verde],['egreso','↓ Egreso',C.error]].map(([v,l,c])=>(
              <button key={v} onClick={()=>setForm(f=>({...f,tipomovimiento:v}))} style={{flex:1,padding:'10px',borderRadius:10,border:`2px solid ${form.tipomovimiento===v?c:C.borde}`,background:form.tipomovimiento===v?c+'18':C.blanco,color:form.tipomovimiento===v?c:C.textoSub,fontWeight:700,fontSize:14}}>{l}</button>
            ))}
          </div>
          <span style={labelSt}>MEDIO DE PAGO</span>
          <RadioPago value={form.mediodepago} onChange={v=>setForm(f=>({...f,mediodepago:v}))}/>
          <span style={labelSt}>DESCRIPCIÓN</span>
          <input style={inputSt} value={form.descmovimiento} onChange={e=>setForm(f=>({...f,descmovimiento:e.target.value}))} placeholder="Motivo del movimiento" autoFocus/>
          <span style={labelSt}>MONTO ($)</span>
          <input style={inputSt} type="number" value={form.monto} onChange={e=>setForm(f=>({...f,monto:e.target.value}))} placeholder="0"/>
          <Btn onClick={guardarMov} disabled={loading}>{loading?'Guardando...':'Registrar Movimiento'}</Btn>
        </Modal>
      )}

      {modalCierre&&(
        <ModalCierreCaja apertura={apertura} datos={cajaData} onClose={()=>setModalCierre(false)} showToast={showToast} onCerrada={onRefresh}/>
      )}
    </div>
  );
}

// ─── REPORTES ─────────────────────────────────────────────────────────────────
// Primer día del mes actual, en formato YYYY-MM-DD (para el filtro de período por defecto)
function primerDiaMes(){const d=new Date();return new Date(d.getFullYear(),d.getMonth(),1).toISOString().split('T')[0];}
function hoyStr(){return new Date().toISOString().split('T')[0];}
function primerDiaMesAnterior(){const d=new Date();return new Date(d.getFullYear(),d.getMonth()-1,1).toISOString().split('T')[0];}
function ultimoDiaMesAnterior(){const d=new Date();return new Date(d.getFullYear(),d.getMonth(),0).toISOString().split('T')[0];}

const METRICAS_COMPARAR=[['ventas','💰 Ventas'],['compras','📦 Compras'],['ganancia','📈 Ganancia']];

function ReportesTab({onNuevoProducto}) {
  const [tab,setTab]=useState('hoy');
  const [reporte,setReporte]=useState(null);
  const [caja,setCaja]=useState(null);
  const [mensual,setMensual]=useState(null);
  const [rentabilidad,setRentabilidad]=useState(null);
  const [productos,setProductos]=useState([]);
  const [toast,setToast]=useState(null);
  // Filtro de período compartido por los reportes "Mensual" y "Rentabilidad"
  const [desde,setDesde]=useState(primerDiaMes());
  const [hasta,setHasta]=useState(hoyStr());
  // Comparador de períodos: métrica + dos rangos de fecha independientes
  const [metricComparar,setMetricComparar]=useState('ventas');
  const [desdeA,setDesdeA]=useState(primerDiaMesAnterior());
  const [hastaA,setHastaA]=useState(ultimoDiaMesAnterior());
  const [desdeB,setDesdeB]=useState(primerDiaMes());
  const [hastaB,setHastaB]=useState(hoyStr());
  const [comparar,setComparar]=useState(null);
  const showToast=(msg,type='ok')=>setToast({msg,type});
  const loadProductos=()=>api.get('/productos').then(d=>setProductos(Array.isArray(d)?d:[]));
  useEffect(()=>{api.get('/reportes/diario').then(setReporte);api.get('/caja/hoy').then(setCaja);loadProductos();},[]);
  useEffect(()=>{
    if(tab==='mensual')api.get(`/reportes/mensual?desde=${desde}&hasta=${hasta}`).then(setMensual);
    if(tab==='rentabilidad')api.get(`/reportes/rentabilidad?desde=${desde}&hasta=${hasta}`).then(setRentabilidad);
  },[tab,desde,hasta]);
  useEffect(()=>{
    if(tab!=='comparar')return;
    setComparar(null);
    api.get(`/reportes/comparar?metric=${metricComparar}&desde1=${desdeA}&hasta1=${hastaA}&desde2=${desdeB}&hasta2=${hastaB}`).then(setComparar);
  },[tab,metricComparar,desdeA,hastaA,desdeB,hastaB]);

  const filtroPeriodo=(
    <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:14,alignItems:'center'}}>
      <div style={{display:'flex',alignItems:'center',gap:6}}>
        <span style={{fontSize:12,color:C.textoSub,fontWeight:600}}>Desde</span>
        <input type="date" style={{...inputSt,marginBottom:0,width:150}} value={desde} onChange={e=>setDesde(e.target.value)}/>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:6}}>
        <span style={{fontSize:12,color:C.textoSub,fontWeight:600}}>Hasta</span>
        <input type="date" style={{...inputSt,marginBottom:0,width:150}} value={hasta} onChange={e=>setHasta(e.target.value)}/>
      </div>
      <BtnSm outline color={C.textoSub} onClick={()=>{setDesde(primerDiaMes());setHasta(hoyStr());}}>Limpiar</BtnSm>
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',flex:1,overflow:'hidden',minHeight:0}}>
      {toast&&<Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
      <SubTab tabs={[['hoy','📅 Hoy'],['mensual','📈 Período'],['rentabilidad','💹 Rentabilidad'],['comparar','📊 Comparar'],['productos','🥩 Productos']]} active={tab} onChange={setTab}/>
      <div style={{overflowY:'auto',padding:16,flex:1,background:C.crema,minHeight:0}}>
        {tab==='hoy'&&reporte&&caja&&(
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:12}}>
            {[
              {label:'VENTAS HOY',  value:fmt(reporte.totalVentas),  sub:`${reporte.cantidadVentas} ventas`, color:C.verde, bg:C.verdeBg},
              {label:'COMPRAS HOY', value:fmt(reporte.totalCompras), sub:`${reporte.cantidadCompras} compras`,color:C.error, bg:C.errorBg},
              {label:'GANANCIA',    value:fmt(reporte.ganancia),     sub:'Ventas − Compras',                 color:C.rojo,  bg:C.amarilloBg},
              {label:'SALDO CAJA',  value:fmt(caja.saldo),           sub:`↑${fmt(caja.ingresos)} ↓${fmt(caja.egresos)}`,color:C.rojo,bg:C.blanco},
            ].map(s=>(
              <div key={s.label} style={{background:s.bg,borderRadius:14,padding:18,border:`2px solid ${s.color}33`,boxShadow:C.sombra}}>
                <div style={{fontSize:10,color:s.color,fontWeight:800,letterSpacing:1.5}}>{s.label}</div>
                <div style={{fontSize:24,fontWeight:900,color:s.color,marginTop:6}}>{s.value}</div>
                <div style={{fontSize:11,color:C.textoSub,marginTop:4}}>{s.sub}</div>
              </div>
            ))}
          </div>
        )}
        {tab==='mensual'&&(
          <>
            {filtroPeriodo}
            {mensual?(
              <>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:12,marginBottom:14}}>
                  {[
                    {label:'VENTAS',   value:fmt(mensual.totalVentas), color:C.verde, bg:C.verdeBg},
                    {label:'COMPRAS',  value:fmt(mensual.totalCompras||0), color:C.error, bg:C.errorBg},
                    {label:'GANANCIA', value:fmt(mensual.ganancia||0), color:C.rojo,  bg:C.amarilloBg},
                  ].map(s=>(
                    <div key={s.label} style={{background:s.bg,borderRadius:14,padding:16,border:`2px solid ${s.color}33`}}>
                      <div style={{fontSize:10,color:s.color,fontWeight:800,letterSpacing:1.5}}>{s.label} (PERÍODO)</div>
                      <div style={{fontSize:22,fontWeight:900,color:s.color,marginTop:4}}>{s.value}</div>
                    </div>
                  ))}
                </div>
                {mensual.ventasDiarias?.length>0&&(
                  <div style={{background:C.blanco,borderRadius:14,padding:18,marginBottom:14,border:`1.5px solid ${C.borde}`}}>
                    <div style={{fontWeight:900,marginBottom:14,color:C.rojo}}>Ventas por día</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={mensual.ventasDiarias.map(d=>({día:d._id,total:d.total}))}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.borde}/>
                        <XAxis dataKey="día" stroke={C.textoSub} fontSize={10}/>
                        <YAxis stroke={C.textoSub} fontSize={10} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`}/>
                        <Tooltip formatter={v=>[fmt(v),'Total']} contentStyle={{background:C.blanco,border:`1px solid ${C.borde}`,borderRadius:8}}/>
                        <Bar dataKey="total" fill={C.rojo} radius={[5,5,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <div style={{background:C.blanco,borderRadius:14,padding:18,border:`1.5px solid ${C.borde}`}}>
                  <div style={{fontWeight:900,marginBottom:12,color:C.rojo}}>🏆 Top Productos (por monto vendido)</div>
                  {mensual.productosTop?.map((p,i)=>(
                    <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'8px 10px',background:i%2===0?C.crema:'transparent',borderRadius:8,marginBottom:4}}>
                      <span style={{fontSize:13,color:C.textoOsc}}><span style={{color:C.rojo,fontWeight:900,marginRight:6}}>#{i+1}</span>{p._id}</span>
                      <span style={{fontWeight:900,color:C.verde}}>{fmt(p.totalMonto)}</span>
                    </div>
                  ))}
                  {mensual.productosTop?.length===0&&(
                    <div style={{textAlign:'center',color:C.textoSub,padding:20,fontSize:13}}>Sin ventas en este período</div>
                  )}
                </div>
              </>
            ):<div style={{textAlign:'center',color:C.textoSub,padding:40}}>Cargando...</div>}
          </>
        )}
        {tab==='rentabilidad'&&(
          <>
            {filtroPeriodo}
            {rentabilidad?(
              <div style={{background:C.blanco,borderRadius:14,padding:18,border:`1.5px solid ${C.borde}`}}>
                <div style={{fontWeight:900,marginBottom:4,color:C.rojo}}>💹 Ranking por ganancia residual</div>
                <div style={{fontSize:12,color:C.textoSub,marginBottom:14}}>Cantidad vendida × (precio de venta − precio de costo)</div>
                {rentabilidad.ranking?.map((p,i)=>(
                  <div key={p._id||i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 10px',background:i%2===0?C.crema:'transparent',borderRadius:8,marginBottom:4,gap:8}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{fontSize:13,color:C.textoOsc,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        <span style={{color:C.rojo,fontWeight:900,marginRight:6}}>#{i+1}</span>{p.productonombre}
                        {p.costoIncompleto&&<span title="Algunas unidades no tenían costo cargado y no se contaron en la ganancia" style={{marginLeft:6,color:C.error,fontSize:11}}>⚠️</span>}
                      </div>
                      <div style={{fontSize:11,color:C.textoSub,marginTop:2}}>{p.cantidadVendida} unid. · {fmt(p.montoVendido)} vendido · {p.margenPromedio?.toFixed(1)}% margen</div>
                    </div>
                    <span style={{fontWeight:900,color:p.gananciaResidual>=0?C.verde:C.error,whiteSpace:'nowrap'}}>{fmt(p.gananciaResidual)}</span>
                  </div>
                ))}
                {rentabilidad.ranking?.length===0&&(
                  <div style={{textAlign:'center',color:C.textoSub,padding:20,fontSize:13}}>Sin ventas en este período</div>
                )}
              </div>
            ):<div style={{textAlign:'center',color:C.textoSub,padding:40}}>Cargando...</div>}
          </>
        )}
        {tab==='comparar'&&(
          <>
            <div style={{marginBottom:14}}>
              <span style={{fontSize:12,color:C.textoSub,fontWeight:600,marginRight:8}}>Indicador a comparar</span>
              <select style={{...inputSt,marginBottom:0,width:180,display:'inline-block'}} value={metricComparar} onChange={e=>setMetricComparar(e.target.value)}>
                {METRICAS_COMPARAR.map(([k,l])=><option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(230px,1fr))',gap:12,marginBottom:14}}>
              <div style={{background:C.blanco,borderRadius:14,padding:14,border:`1.5px solid ${C.azul}55`}}>
                <div style={{fontSize:11,fontWeight:800,color:C.azul,marginBottom:8,letterSpacing:0.5}}>🅰️ PERÍODO A</div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <span style={{fontSize:12,color:C.textoSub}}>Desde</span>
                    <input type="date" style={{...inputSt,marginBottom:0,width:140}} value={desdeA} onChange={e=>setDesdeA(e.target.value)}/>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <span style={{fontSize:12,color:C.textoSub}}>Hasta</span>
                    <input type="date" style={{...inputSt,marginBottom:0,width:140}} value={hastaA} onChange={e=>setHastaA(e.target.value)}/>
                  </div>
                </div>
              </div>
              <div style={{background:C.blanco,borderRadius:14,padding:14,border:`1.5px solid ${C.rojo}55`}}>
                <div style={{fontSize:11,fontWeight:800,color:C.rojo,marginBottom:8,letterSpacing:0.5}}>🅱️ PERÍODO B</div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <span style={{fontSize:12,color:C.textoSub}}>Desde</span>
                    <input type="date" style={{...inputSt,marginBottom:0,width:140}} value={desdeB} onChange={e=>setDesdeB(e.target.value)}/>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <span style={{fontSize:12,color:C.textoSub}}>Hasta</span>
                    <input type="date" style={{...inputSt,marginBottom:0,width:140}} value={hastaB} onChange={e=>setHastaB(e.target.value)}/>
                  </div>
                </div>
              </div>
            </div>
            {comparar?(
              <>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:12,marginBottom:14}}>
                  {[
                    {label:`PERÍODO A · ${comparar.periodoA.desde} a ${comparar.periodoA.hasta}`,value:fmt(comparar.periodoA.total),color:C.azul,bg:C.azulBg},
                    {label:`PERÍODO B · ${comparar.periodoB.desde} a ${comparar.periodoB.hasta}`,value:fmt(comparar.periodoB.total),color:C.rojo,bg:C.amarilloBg},
                    {label:'VARIACIÓN B vs A',value:`${comparar.variacion.porcentual>=0?'↑':'↓'} ${Math.abs(comparar.variacion.porcentual).toFixed(1)}%`,color:C.textoOsc,bg:C.crema},
                  ].map(s=>(
                    <div key={s.label} style={{background:s.bg,borderRadius:14,padding:14,border:`2px solid ${s.color}33`}}>
                      <div style={{fontSize:10,color:s.color,fontWeight:800,letterSpacing:0.8}}>{s.label}</div>
                      <div style={{fontSize:20,fontWeight:900,color:s.color,marginTop:4}}>{s.value}</div>
                    </div>
                  ))}
                </div>
                <div style={{background:C.blanco,borderRadius:14,padding:18,border:`1.5px solid ${C.borde}`}}>
                  <div style={{fontWeight:900,marginBottom:4,color:C.rojo}}>
                    {METRICAS_COMPARAR.find(([k])=>k===metricComparar)?.[1]} por día — Período A vs Período B
                  </div>
                  <div style={{fontSize:12,color:C.textoSub,marginBottom:14}}>Alineado por día del período (Día 1 = primer día de cada rango)</div>
                  {comparar.serie.length>0?(
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={comparar.serie.map(s=>({dia:`Día ${s.dia}`,'Período A':s.valorA,'Período B':s.valorB}))}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.borde}/>
                        <XAxis dataKey="dia" stroke={C.textoSub} fontSize={10}/>
                        <YAxis stroke={C.textoSub} fontSize={10} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`}/>
                        <Tooltip formatter={v=>[v==null?'—':fmt(v),'']} contentStyle={{background:C.blanco,border:`1px solid ${C.borde}`,borderRadius:8}}/>
                        <Legend wrapperStyle={{fontSize:12}}/>
                        <Bar dataKey="Período A" fill={C.azul} radius={[4,4,0,0]}/>
                        <Bar dataKey="Período B" fill={C.rojo} radius={[4,4,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  ):(
                    <div style={{textAlign:'center',color:C.textoSub,padding:20,fontSize:13}}>Sin datos para graficar</div>
                  )}
                </div>
              </>
            ):<div style={{textAlign:'center',color:C.textoSub,padding:40}}>Cargando...</div>}
          </>
        )}
        {tab==='productos'&&(
          <>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
              <span style={{fontWeight:900,fontSize:15,color:C.rojo}}>PRODUCTOS ({productos.length})</span>
              <BtnSm onClick={onNuevoProducto}>+ Nuevo producto</BtnSm>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:8}}>
              {productos.map(p=>{const tc=tipoColor[p.productotipo]||tipoColor.otro;return(
                <div key={p._id} style={{background:C.blanco,borderRadius:10,padding:'12px 14px',border:`1.5px solid ${C.borde}`,display:'flex',justifyContent:'space-between',alignItems:'center',boxShadow:C.sombra}}>
                  <span style={{fontWeight:700,color:C.textoOsc}}>{p.productonombre}</span>
                  <span style={{background:tc.bg,color:tc.text,borderRadius:6,padding:'2px 9px',fontSize:11,fontWeight:800}}>{p.productotipo}</span>
                </div>
              );})}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── APP PRINCIPAL ────────────────────────────────────────────────────────────
export default function App() {
  const [user,setUser]=useState(null);
  const [nav,setNav]=useState('ventas');
  const [modalPassword,setModalPassword]=useState(false);
  const [modalProducto,setModalProducto]=useState(false);
  const [modalApertura,setModalApertura]=useState(false);
  const [modalRedondeo,setModalRedondeo]=useState(false);
  const [cajaData,setCajaData]=useState(null);
  const [toast,setToast]=useState(null);
  // navKey fuerza remount del tab activo al cambiar de sección
  // userKey fuerza remount de todo al cambiar de usuario
  const [navKey,setNavKey]=useState(0);
  const [userKey,setUserKey]=useState(0);
  const showToast=(msg,type='ok')=>setToast({msg,type});

  const loadCaja=useCallback(()=>{
    api.get('/caja/apertura/hoy').then(d=>setCajaData(d));
  },[]);

  useEffect(()=>{
    if('serviceWorker' in navigator)
      navigator.serviceWorker.getRegistrations().then(regs=>regs.forEach(r=>r.unregister()));
    const token=localStorage.getItem('token');
    const nombre=localStorage.getItem('userName');
    if(token){setUser({token,nombre});loadCaja();}
  },[loadCaja]);

  // Recargar caja cada 5 minutos para mantener datos frescos
  useEffect(()=>{
    if(!user) return;
    const interval=setInterval(loadCaja, 5*60*1000);
    return ()=>clearInterval(interval);
  },[user,loadCaja]);

  // Reload al cambiar de tab — siempre recargar caja y ventas
  const handleNav = useCallback((newNav) => {
    setNav(newNav);
    setNavKey(k=>k+1);          // fuerza remount del tab destino
    if(newNav==='caja') loadCaja(); // siempre actualizar caja al entrar
  },[loadCaja]);

  const logout=()=>{
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('userName_raw');
    // NO limpiar cajaData — la caja pertenece al negocio, no al usuario
    setNav('ventas');
    setNavKey(0);
    setUser(null);
  };

  // Al hacer login: resetear userKey para forzar remount completo de tabs
  const handleLogin=(u)=>{
    setUserKey(k=>k+1);   // invalida datos del usuario anterior
    setNavKey(0);
    setNav('ventas');
    setUser(u);
    loadCaja(); // la caja es del negocio, no del usuario — siempre cargar
  };

  const cajaAbierta=cajaData?.apertura?.estado==='abierta';

  if(!user) return <><style>{globalCSS}</style><Login onLogin={handleLogin}/></>;

  return (
    <>
      <style>{globalCSS}</style>
      {toast&&<Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}

      {modalPassword&&<ModalCambiarPassword onClose={()=>setModalPassword(false)} showToast={showToast}/>}
      {modalRedondeo&&<ModalConfigRedondeo onClose={()=>setModalRedondeo(false)} showToast={showToast}/>}
      {modalProducto&&<ModalNuevoProducto onClose={()=>setModalProducto(false)} showToast={showToast} onCreated={()=>setNavKey(k=>k+1)}/>}
      {modalApertura&&<ModalAperturaCaja onClose={()=>setModalApertura(false)} showToast={showToast} onAbierta={loadCaja}/>}

      <div style={{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'}}>
        <Header userName={user.nombre} onLogout={logout} onCambiarPassword={()=>setModalPassword(true)} onConfigRedondeo={()=>setModalRedondeo(true)} cajaAbierta={cajaAbierta}/>

        <div style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column',minHeight:0}}>
          {nav==='ventas'   &&<VentasTab   key={`ventas-${userKey}-${navKey}`}   onNuevoProducto={()=>setModalProducto(true)} cajaAbierta={cajaAbierta}/>}
          {nav==='compras'  &&<ComprasTab  key={`compras-${userKey}-${navKey}`}/>}
          {nav==='caja'     &&(
            cajaData?.apertura
              ?<CajaTab key={`caja-${userKey}-${navKey}`} cajaData={cajaData} onRefresh={loadCaja} showToast={showToast}/>
              :<CajaCerradaScreen cajaData={cajaData} onAbrir={()=>setModalApertura(true)} onReabrir={loadCaja} showToast={showToast}/>
          )}
          {nav==='reportes' &&<ReportesTab key={`reportes-${userKey}-${navKey}`} onNuevoProducto={()=>setModalProducto(true)}/>}
        </div>

        <NavBar active={nav} onChange={handleNav}/>
      </div>
    </>
  );
}
